import {omneticApi} from '@dms/api/base';
import {UuidResponseBody, UuidResponseBodySchema} from '@dms/api/shared';

import {
  ChangeWorkshopInterestBranchApiArg,
  ChangeWorkshopInterestBranchRequestBodySchema,
  ChangeWorkshopInterestContactPersonApiArg,
  ChangeWorkshopInterestContactPersonRequestBodySchema,
  CreateWorkshopInterestApiArg,
  CreateWorkshopInterestRequestBodySchema,
  DeleteWorkshopInterestApiArg,
  EndWorkshopInterestApiArg,
  EndWorkshopInterestRequestBodySchema,
  GetWorkshopInterestApiArg,
  GetWorkshopInterestApiArgSchema,
  GetWorkshopInterestApiResponse,
  ListWorkshopInterestActivitiesApiArg,
  ListWorkshopInterestActivitiesApiArgSchema,
  ListWorkshopInterestActivitiesApiResponse,
  OpenWorkshopInterestApiArg,
  SetWorkshopInterestSourceApiArg,
  SetWorkshopInterestSourceRequestBodySchema,
  WorkshopInterestActivityResponseBodySchema,
  WorkshopInterestResponseBodySchema,
} from './types';

const injected = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    createWorkshopInterest: build.mutation<UuidResponseBody, CreateWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest`,
        method: 'POST',
        body: queryArg.createWorkshopInterestRequestBody,
        queryArg,
      }),
      invalidatesTags: ['WorkshopInterest'],
      extraOptions: {
        requestSchema: CreateWorkshopInterestRequestBodySchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),

    getWorkshopInterest: build.query<GetWorkshopInterestApiResponse, GetWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: GetWorkshopInterestApiArgSchema,
        responseSchema: WorkshopInterestResponseBodySchema,
      },
    }),

    changeWorkshopInterestBranch: build.mutation<void, ChangeWorkshopInterestBranchApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/change-branch`,
        method: 'POST',
        body: queryArg.changeWorkshopInterestBranchRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: ChangeWorkshopInterestBranchRequestBodySchema,
      },
    }),

    changeWorkshopInterestContactPerson: build.mutation<
      void,
      ChangeWorkshopInterestContactPersonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/change-contact-person`,
        method: 'POST',
        body: queryArg.changeWorkshopInterestContactPersonRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'WorkshopInterest', id: queryArg.id},
        'Customer',
      ],
      extraOptions: {
        requestSchema: ChangeWorkshopInterestContactPersonRequestBodySchema,
      },
    }),

    setWorkshopInterestSource: build.mutation<void, SetWorkshopInterestSourceApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/source`,
        method: 'PUT',
        body: queryArg.setWorkshopInterestSourceRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: SetWorkshopInterestSourceRequestBodySchema,
      },
    }),

    listWorkshopInterestActivities: build.query<
      ListWorkshopInterestActivitiesApiResponse,
      ListWorkshopInterestActivitiesApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.interestId}/activity`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'WorkshopInterest', id: queryArg.interestId},
      ],
      extraOptions: {
        requestSchema: ListWorkshopInterestActivitiesApiArgSchema,
        responseSchema: WorkshopInterestActivityResponseBodySchema.array(),
      },
    }),

    deleteWorkshopInterest: build.mutation<void, DeleteWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}`,
        method: 'DELETE',
        queryArg,
      }),
      invalidatesTags: ['WorkshopInterest'],
    }),

    openWorkshopInterest: build.mutation<void, OpenWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/open`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
    }),

    endWorkshopInterest: build.mutation<void, EndWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/end`,
        method: 'POST',
        body: queryArg.endWorkshopInterestRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: EndWorkshopInterestRequestBodySchema,
      },
    }),
  }),
});

export const {
  useCreateWorkshopInterestMutation,
  useGetWorkshopInterestQuery,
  useChangeWorkshopInterestBranchMutation,
  useChangeWorkshopInterestContactPersonMutation,
  useSetWorkshopInterestSourceMutation,
  useListWorkshopInterestActivitiesQuery,
  useDeleteWorkshopInterestMutation,
  useOpenWorkshopInterestMutation,
  useEndWorkshopInterestMutation,
} = injected;
